import { cn } from '@/lib/utils'
import { MapPin } from 'lucide-react'
import { SkeletonRegion, useElementSize, useStableKeys } from './_shared'

export interface MapSkeletonProps {
	/** Fixed marker count. Omit to scale the count to the measured area. */
	markers?: number
	height?: string
	className?: string
	label?: string | false
}

// Roughly one marker per this much panel area, so tiny panels don't look peppered.
const AREA_PER_MARKER = 26000

const MARKER_POSITIONS = [
	'left-1/4 top-1/3',
	'left-1/2 top-1/4',
	'left-2/3 top-1/2',
	'left-1/3 top-2/3',
	'left-3/4 top-1/3',
	'left-1/5 top-1/2',
]

export function MapSkeleton({ markers, height, className, label = 'Loading map' }: MapSkeletonProps) {
	const [rootRef, size] = useElementSize<HTMLDivElement>()
	const area = size.width * size.height
	const markerCount = markers ?? (area > 0 ? Math.min(6, Math.max(1, Math.round(area / AREA_PER_MARKER))) : 5)
	const keys = useStableKeys(markerCount)
	return (
		<SkeletonRegion
			ref={rootRef}
			label={label}
			className={cn('bg-muted relative size-full min-h-0 overflow-hidden rounded-lg', height, className)}>
			{keys.map((key, i) => (
				<MapPin
					key={key}
					className={cn(
						'absolute size-6 animate-pulse text-muted-foreground/35 fill-muted-foreground/15',
						MARKER_POSITIONS[i % MARKER_POSITIONS.length],
					)}
				/>
			))}
		</SkeletonRegion>
	)
}
